import React, { useState, useCallback, useEffect } from "react";
import { Routes, Route } from "react-router-dom";
import {
  LandingView,
  PayWall,
  PartnerApp,
  InstallerApp,
  CommercialApp,
  LoginModal,
  AccessManagerModal,
  CRMHelpBot,
} from "./components";
import { MentionsLegales } from "./components/legal/MentionsLegales";
import { CGU } from "./components/legal/CGU";
import { Confidentialite } from "./components/legal/Confidentialite";
import { AdminDashboard } from "./components/AdminDashboard";
import { ConfirmationModal } from "./components/ui";
import type { User, UserRole, Job, ProfileRow, JobRow } from "./types";
import { profileToUser, jobRowToJob } from "./types";
import { useAuth } from "./hooks/useAuth";
import { useSubscription } from "./hooks/useSubscription";
import { supabase } from "./supabaseClient";
import { useJobs } from "./hooks/useJobs";
import { usePartnerInstallerJobs } from "./hooks/usePartnerInstallerJobs";
import { invokeEdgeFunction } from "./utils/invokeEdgeFunction";
import { useInventory } from "./hooks/useInventory";
import { useStockRegions } from "./hooks/useStockRegions";
import { usePartnerStockRegions } from "./hooks/usePartnerStockRegions";
import { useLogs } from "./hooks/useLogs";
import { useProfile } from "./hooks/useProfile";
import { useProfiles } from "./hooks/useProfiles";
import { useTeam } from "./hooks/useTeam";
import { useBudgetPrices } from "./hooks/useBudgetPrices";
import { computeSecretaryNotifications } from "./utils/secretaryNotifications";
import { getBranding } from "./config/branding";
import JSZip from "jszip";
import tdrFavicon from "./assets/tdr-solution-logo-transparent.png";

const SESSION_STORAGE_KEY = "crm-led-session";

/** Appelle l'Edge Function notify-webhooks (fire-and-forget). */
async function notifyWebhooks(
  event: "job.created" | "job.updated" | "job.completed",
  jobId: string,
  payload: { id: string; clientName?: string; status?: string; [k: string]: unknown }
): Promise<void> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) return;
    const url = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/notify-webhooks`;
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ event, jobId, payload }),
    });
    if (!res.ok) {
      console.warn("[notifyWebhooks] Échec:", res.status, event, jobId);
    }
  } catch (e) {
    console.warn("[notifyWebhooks] Erreur:", e instanceof Error ? e.message : String(e));
  }
}

/** Exception pour les démos : cet email contourne la vérification PayWall. */
const DEMO_BYPASS_EMAIL = "mendyzerbib1606@gmail.com";

/** Désactive complètement le Paywall : aucun blocage par abonnement / période d'essai. */
const PAYWALL_DISABLED = true;

/**
 * Date de clémence (grandfather) : les comptes dont l'abonnement a été créé AVANT cette date
 * restent autorisés même sans abonnement payant. Pour réactiver le Paywall sans bloquer les
 * clients actuels : mettre PAYWALL_DISABLED = false et GRANDFATHER_CUTOFF = date du jour (ex: "2025-03-01T00:00:00.000Z").
 * Les nouveaux inscrits après cette date passeront par l'essai puis le Paywall.
 */
const GRANDFATHER_CUTOFF = "2025-12-31T23:59:59.000Z";

function SetPasswordScreen({
  onSetPassword,
  error,
  isRecovery,
}: {
  onSetPassword: (password: string, confirmPassword: string) => void;
  error: string | null;
  isRecovery?: boolean;
}) {
  const [password, setPassword] = React.useState("");
  const [confirm, setConfirm] = React.useState("");
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSetPassword(password, confirm);
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 dark:bg-slate-900 p-4 relative">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-400/20 dark:bg-blue-600/20 blur-[120px] rounded-full" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-400/20 dark:bg-purple-600/20 blur-[120px] rounded-full" />
      </div>
      <div className="w-full max-w-md rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-slate-800 dark:backdrop-blur-xl p-8 shadow-2xl relative z-10">
        <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
          {isRecovery ? "Réinitialisez votre mot de passe" : "Créez votre mot de passe"}
        </h1>
        <p className="text-gray-600 dark:text-slate-300 text-sm mb-6">
          {isRecovery
            ? "Choisissez un nouveau mot de passe pour vous connecter."
            : "Vous avez été invité à rejoindre l'équipe. Choisissez un mot de passe pour vous connecter à l'avenir."}
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
              Mot de passe
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="••••••••"
              autoComplete="new-password"
              minLength={6}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
              Confirmer le mot de passe
            </label>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="••••••••"
              autoComplete="new-password"
              minLength={6}
            />
          </div>
          {error && (
            <p className="text-sm text-red-500 dark:text-red-400">{error}</p>
          )}
          <button
            type="submit"
            className="w-full py-3 rounded-xl bg-gradient-to-r from-blue-600 to-blue-500 text-white font-medium shadow-lg shadow-blue-500/30 hover:opacity-95 transition-opacity"
          >
            Valider et accéder au CRM
          </button>
        </form>
      </div>
    </div>
  );
}

export default function App() {
  const [currentView, setCurrentView] = useState<
    "landing" | "admin" | "partner" | "installer" | "commercial"
  >("landing");
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<UserRole | null>(null);
  const [accessManagerConfig, setAccessManagerConfig] = useState<{
    isOpen: boolean;
    type: "partner" | "installer" | "commercial";
  }>({ isOpen: false, type: "partner" });
  const [confirmConfig, setConfirmConfig] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    action: () => void | Promise<unknown>;
  }>({ isOpen: false, title: "", message: "", action: () => {} });
  /** Affiché quand l'utilisateur doit définir un mot de passe (invitation ou lien reset password). */
  const [needsToSetPassword, setNeedsToSetPassword] = useState(false);
  /** true = mot de passe oublié (PASSWORD_RECOVERY), false = invitation équipe. */
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);
  const [setPasswordError, setSetPasswordError] = useState<string | null>(null);
  /** Navigation demandée depuis le bot (mini secrétaire) vers un onglet du dashboard admin */
  const [adminPendingNavigation, setAdminPendingNavigation] = useState<{
    tab: string;
    regionId?: string;
    partnerId?: string;
  } | null>(null);

  // Onglet navigateur dynamique selon le domaine (white-label).
  useEffect(() => {
    if (typeof document === "undefined") return;
    const branding = getBranding();
    document.title = branding.browserTitle;

    const isTdrDomain = window.location.hostname
      .toLowerCase()
      .includes("tdrsolution-crm.com");
    if (!isTdrDomain) return;

    const faviconLink = document.querySelector("link[rel='icon']") as HTMLLinkElement | null;
    if (!faviconLink) return;
    faviconLink.href = tdrFavicon;
    faviconLink.type = "image/png";
  }, []);

  const { loginAdmin, loginWithCode, registerAdmin, logout } = useAuth();

  // Écoute des événements auth : PASSWORD_RECOVERY = clic sur le lien "mot de passe oublié".
  // SIGNED_OUT / TOKEN_REFRESHED failed = session expirée → redirection propre vers login sans crasher.
  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event: string) => {
      if (event === "PASSWORD_RECOVERY") {
        setIsPasswordRecovery(true);
        setNeedsToSetPassword(true);
      }
      if (event === "SIGNED_OUT") {
        setCurrentUser(null);
        setCurrentView("landing");
        setModalOpen(true);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  // Callback après invitation : le lien d'invitation redirige ici avec les tokens dans l'URL.
  // On établit la session du membre invité (remplace celle de l'admin si même navigateur) puis on nettoie l'URL.
  // En local : ajouter l'URL (ex. http://localhost:3000) dans Supabase → Auth → URL Configuration → Redirect URLs.
  useEffect(() => {
    let cancelled = false;
    const isDev = typeof window !== "undefined" && (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");
    (async () => {
      const hash = window.location.hash?.slice(1) || "";
      const search = window.location.search?.slice(1) || "";
      const params = new URLSearchParams(hash || search);
      let accessToken = params.get("access_token");
      let refreshToken = params.get("refresh_token");
      let code = params.get("code");
      if (!accessToken && !code) {
        const q = new URLSearchParams(search);
        accessToken = accessToken || q.get("access_token");
        refreshToken = refreshToken || q.get("refresh_token");
        code = code || q.get("code");
      }
      if (isDev && (accessToken || code)) {
        console.log("[Auth] Retour redirect détecté (hash ou query), restauration session…");
      }

      const restoreFromSession = async (): Promise<boolean> => {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) return false;
        const uid = session.user.id;
        const { data: prof, error } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", uid)
          .maybeSingle();
        if (cancelled || error || !prof) return false;
        const role = (prof as ProfileRow).role;
        if (role === "admin") {
          setCurrentUser(profileToUser(prof as ProfileRow));
          setCurrentView("admin");
          return true;
        }
        if (role === "partner" || role === "installer" || role === "commercial") {
          setCurrentUser(profileToUser(prof as ProfileRow));
          setCurrentView(role);
          sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({
            role: (prof as ProfileRow).role,
            code: (prof as ProfileRow).code,
          }));
          return true;
        }
        return false;
      };

      if (code) {
        try {
          const { error } = await supabase.auth.exchangeCodeForSession(code);
          if (!cancelled && !error) {
            const type = new URLSearchParams(window.location.search).get("type");
            if (type === "invite" || type === "recovery") {
              setIsPasswordRecovery(type === "recovery");
              setNeedsToSetPassword(true);
            } else {
              const restored = await restoreFromSession();
              if (restored) {
                if (isDev) console.log("[Auth] Session restaurée (code exchange).");
                if (!cancelled) setAuthReady(true);
                window.history.replaceState(null, "", "/");
                return;
              }
              if (isDev) {
                const { data: { session } } = await supabase.auth.getSession();
                console.warn("[Auth] exchangeCodeForSession OK mais profil non trouvé. uid:", session?.user?.id);
              }
            }
            window.history.replaceState(null, "", "/");
          }
        } catch (error) {
          console.error("Erreur exchangeCodeForSession:", error);
        }
      } else if (accessToken && refreshToken) {
        try {
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          if (!cancelled && !error) {
            const type = params.get("type");
            if (type === "invite" || type === "recovery") {
              setIsPasswordRecovery(type === "recovery");
              setNeedsToSetPassword(true);
            } else {
              const restored = await restoreFromSession();
              if (restored) {
                if (isDev) console.log("[Auth] Session poseur/partenaire restaurée.");
                if (!cancelled) setAuthReady(true);
                window.history.replaceState(null, "", "/");
                return;
              }
              if (isDev) {
                const { data: { session } } = await supabase.auth.getSession();
                console.warn("[Auth] setSession OK mais profil non trouvé. uid:", session?.user?.id, "→ Vérifiez RLS et que profiles.id = auth.uid().");
              }
            }
            window.history.replaceState(null, "", "/");
          }
        } catch (error) {
          console.error("Erreur setSession:", error);
        }
      }

      if (cancelled) return;

      // Restauration de la session au chargement (évite la déconnexion au refresh)
      let restored = false;
      const { data: { session } } = await supabase.auth.getSession();
      if (cancelled) return;
      if (session?.user) {
        const uid = session.user.id;
        const { data: prof, error } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", uid)
          .maybeSingle();
        if (!cancelled && !error && prof) {
          const role = (prof as ProfileRow).role;
          if (role === "admin") {
            setCurrentUser(profileToUser(prof as ProfileRow));
            setCurrentView("admin");
            restored = true;
          } else if (role === "partner" || role === "installer" || role === "commercial") {
            setCurrentUser(profileToUser(prof as ProfileRow));
            setCurrentView(role);
            restored = true;
          }
        }
      }
      if (!restored) {
        try {
          const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
          if (stored) {
            const { role, code } = JSON.parse(stored) as {
              role?: "partner" | "installer" | "commercial";
              code?: string;
            };
            if (role && code) {
              const result = await loginWithCode(role, code);
              if (!cancelled && result.user) {
                setCurrentUser(result.user);
                setCurrentView(result.user.role);
              } else if (!result.user) {
                sessionStorage.removeItem(SESSION_STORAGE_KEY);
              }
            }
          }
        } catch {
          sessionStorage.removeItem(SESSION_STORAGE_KEY);
        }
      }
      if (!cancelled) setAuthReady(true);
    })();
    return () => { cancelled = true; };
  }, [loginWithCode]);

  // MULTI-TENANT: ID de la "maison" (tenant) pour les données.
  // - Owner (admin avec is_owner) : sa maison = son id.
  // - Membre d'équipe (admin avec owner_id) : même maison que l'owner = owner_id (même stock, partenaires, poseurs, dossiers, etc.).
  // - Partenaire/Poseur : maison de l'admin qui les a créés = owner_id.
  const ownerId = currentUser?.role === 'admin'
    ? (currentUser.is_owner ? currentUser.id : (currentUser.owner_id ?? currentUser.id))
    : (currentUser?.owner_id ?? undefined);

  const subscription = useSubscription(ownerId);

  const isLocalhost = typeof window !== "undefined" && window.location.hostname === "localhost";
  const canBypassPaywall =
    currentUser?.email === DEMO_BYPASS_EMAIL || isLocalhost;
  const isConnectedView = currentView !== "landing";
  const _mustShowPaywall =
    Boolean(currentUser && ownerId && !subscription.loading && !subscription.isAllowed && !canBypassPaywall);
  const mustShowPaywall = PAYWALL_DISABLED ? false : _mustShowPaywall;
  const isGrandfathered = Boolean(
    subscription.subscription?.createdAt && new Date(subscription.subscription.createdAt) < new Date(GRANDFATHER_CUTOFF)
  );
  const isAllowedAccess =
    subscription.isAllowed || canBypassPaywall || PAYWALL_DISABLED || isGrandfathered;

  // Après retour de Stripe Checkout (success_url), synchroniser l'abonnement en base puis recharger
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get("checkout");
    const sessionId = params.get("session_id");
    if (checkout !== "success" || !ownerId) return;

    let cancelled = false;
    (async () => {
      if (sessionId) {
        try {
          await supabase.functions.invoke("sync-checkout-session", {
            body: { session_id: sessionId },
          });
        } catch {
          // ignore (webhook aura peut-être déjà mis à jour)
        }
      }
      if (!cancelled) {
        await subscription.refetch();
        const url = new URL(window.location.href);
        url.searchParams.delete("checkout");
        url.searchParams.delete("session_id");
        window.history.replaceState(null, "", url.pathname + (url.search || ""));
      }
    })();
    return () => { cancelled = true; };
  }, [ownerId, subscription.refetch]);

  const [subscriptionError, setSubscriptionError] = useState<string | null>(null);

  const handleCreateCheckout = useCallback(
    async (targetOwnerId: string): Promise<string | null> => {
      setSubscriptionError(null);
      try {
        const { data: { session }, error: refreshError } = await supabase.auth.refreshSession();
        if (refreshError || !session) throw new Error("Session expirée. Reconnectez-vous.");
        const token = session.access_token;
        const { data, error } = await supabase.functions.invoke("create-checkout-session", {
          body: {
            ownerId: targetOwnerId,
            appUrl: typeof window !== "undefined" ? window.location.origin : undefined,
          },
          headers: { Authorization: `Bearer ${token}` },
        });
        if (error) throw new Error(error.message);
        return data?.url ?? null;
      } catch (e) {
        const message = e instanceof Error ? e.message : String(e);
        console.error("Stripe checkout error:", e);
        setSubscriptionError(message);
        return null;
      }
    },
    []
  );

  const handleDeleteAccount = useCallback(async (): Promise<{ error: string | null }> => {
    try {
      const { data: { session }, error: refreshError } = await supabase.auth.refreshSession();
      if (refreshError || !session) {
        return { error: "Session expirée. Déconnectez-vous et reconnectez-vous puis réessayez." };
      }
      const { data, error } = await supabase.functions.invoke("delete-account", {
        method: "POST",
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (error) {
        return { error: error.message || "Erreur lors de l'appel au serveur." };
      }
      const err = (data as { error?: string })?.error;
      if (err) return { error: err };
      await logout();
      setCurrentUser(null);
      setCurrentView("landing");
      return { error: null };
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      return { error: message };
    }
  }, [logout]);

  const { jobs, create, update, remove, refetch: refetchJobs } = useJobs(ownerId);
  const partnerInstallerJobs = usePartnerInstallerJobs(currentUser, jobs, refetchJobs);
  const inventory = useInventory(ownerId);
  const stockRegions = useStockRegions(ownerId);
  const partnerStockRegions = usePartnerStockRegions(
    (currentUser?.role === "partner" || currentUser?.role === "installer") && !!currentUser,
    currentUser?.role === "partner" || currentUser?.role === "installer" ? currentUser?.code : undefined,
    currentUser?.role === "partner" || currentUser?.role === "installer" ? currentUser?.role : undefined
  );
  const budgetPrices = useBudgetPrices(ownerId ?? undefined);
  const { logs, add: addLog, clear: clearLogs, remove: removeLog } = useLogs(ownerId);
  const { partners, installers, commercials, error: profilesLoadError, addProfile, updateProfile, removeProfile, refetch: refetchProfiles, upsertProfile } = useProfiles(ownerId);
  const team = useTeam(ownerId);
  const ownerProfile = useProfile(ownerId ?? undefined);
  const ownerCompanyName =
    ownerProfile.profile?.preferences?.companyName ??
    ownerProfile.profile?.company ??
    ownerProfile.profile?.name ??
    "";
  const ownerLogoUrl = ownerProfile.profile?.avatar_url ?? undefined;

  const handleExportAllData = useCallback(async (): Promise<{ error: string | null }> => {
    if (!ownerId) return { error: "Espace non identifié." };
    try {
      const regionIds = new Set((stockRegions.regions ?? []).map((r) => r.id));
      const budgetPricesFiltered = (budgetPrices.prices ?? []).filter((p) =>
        regionIds.has(p.stockRegionId)
      );
      const subscriptionExport = subscription.subscription
        ? {
            status: subscription.subscription.status,
            trialEndsAt: subscription.subscription.trialEndsAt,
            currentPeriodEnd: subscription.subscription.currentPeriodEnd ?? null,
          }
        : null;
      const profilesExport = [...(partners ?? []), ...(installers ?? [])];

      const zip = new JSZip();
      zip.file("jobs.json", JSON.stringify(jobs, null, 2));
      zip.file("inventory.json", JSON.stringify(inventory.stock, null, 2));
      zip.file("stock_regions.json", JSON.stringify(stockRegions.regions ?? [], null, 2));
      zip.file("budget_prices.json", JSON.stringify(budgetPricesFiltered, null, 2));
      zip.file("logs.json", JSON.stringify(logs, null, 2));
      zip.file("profiles.json", JSON.stringify(profilesExport, null, 2));
      zip.file("subscription.json", JSON.stringify(subscriptionExport, null, 2));

      const blob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `regie-connect-export-${ownerId.slice(0, 8)}-${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      return { error: null };
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      return { error: message };
    }
  }, [
    ownerId,
    jobs,
    inventory.stock,
    stockRegions.regions,
    budgetPrices.prices,
    logs,
    partners,
    installers,
    subscription.subscription,
  ]);
  // Owner = admin avec is_owner en base, ou admin sans owner_id / owner_id = son id (fallback si is_owner pas encore mis à jour)
  const isOwner =
    currentUser?.is_owner === true ||
    (currentUser?.role === "admin" &&
      (currentUser?.owner_id == null || currentUser?.owner_id === currentUser?.id));

  const addLogEntry = useCallback(
    (action: string, details: string) => {
      addLog(action, currentUser?.name ?? "Système", details);
    },
    [currentUser?.name, addLog]
  );

  const onSelectRole = useCallback((role: UserRole) => {
    setSelectedRole(role);
    setModalOpen(true);
  }, []);

  const handleLoginSuccess = useCallback(
    (user: User) => {
      setCurrentUser(user);
      setCurrentView(user.role);
      addLogEntry("Connexion", `Connexion de ${user.name}`);
      if (user.role === "partner" || user.role === "installer" || user.role === "commercial") {
        sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({ role: user.role, code: user.code }));
      }
    },
    [addLogEntry]
  );

  const handleLogout = useCallback(async () => {
    if (currentUser) addLogEntry("Déconnexion", `Déconnexion de ${currentUser.name}`);
    setCurrentUser(null);
    setCurrentView("landing");
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
    await logout();
  }, [currentUser, addLogEntry, logout]);

  const handlePartnerAddJob = useCallback(
    async (jobData: {
      dossierType?: "led" | "led_exterieur" | "led_interieur" | "deshumidificateur" | "vmc_agricoles" | "th_122" | "renovation" | "hp_bp_flottante" | "pac";
      clientName: string;
      clientPhone: string;
      clientAddress: string;
      surface?: string;
      lightingType?: string;
      ceilingHeight?: string;
      estimatedLedCount?: string;
      dehumidifierCount?: number;
      timerCount?: number;
      needsCounter?: boolean;
      needsGenerator?: boolean;
      notes?: string;
      date?: string;
      time?: string;
      stockRegionId?: string;
    }) => {
      const hasDate = !!jobData.date?.trim();
      const dossierType = jobData.dossierType ?? "led";

      // Partenaires avec code : création toujours via l'Edge Function (contourne RLS / session).
      if (currentUser?.role === "partner" && currentUser?.code) {
        const { error: fnMsg } = await invokeEdgeFunction("create-job-partner", {
          code: currentUser.code,
          role: "partner",
          clientName: jobData.clientName,
          clientPhone: jobData.clientPhone,
          clientAddress: jobData.clientAddress,
          date: jobData.date,
          time: jobData.time,
          stockRegionId: jobData.stockRegionId,
          dossierType: jobData.dossierType,
          surface: jobData.surface,
          lightingType: jobData.lightingType,
          ceilingHeight: jobData.ceilingHeight,
          estimatedLedCount: jobData.estimatedLedCount,
          dehumidifierCount: jobData.dehumidifierCount,
          timerCount: jobData.timerCount,
          needsCounter: jobData.needsCounter,
          needsGenerator: jobData.needsGenerator,
          notes: jobData.notes,
        });
        if (!fnMsg) {
          await refetchJobs();
          await partnerInstallerJobs.refetch();
          addLogEntry("Nouveau dossier", hasDate ? `Créé par ${currentUser.name} (date programmée)` : `Créé par ${currentUser.name}`);
          return { error: null };
        }
        addLogEntry("Erreur", `Création dossier: ${fnMsg}`);
        alert(`Impossible de créer le dossier.\n\n${fnMsg}`);
        return { error: fnMsg };
      }

      // Partenaire sans code (cas rare) : tentative d'insert directe.
      const status: Job["status"] = hasDate ? "scheduled" : "pending";
      const payload: Omit<Job, "id" | "createdAt"> = {
        clientName: jobData.clientName,
        clientPhone: jobData.clientPhone,
        clientAddress: jobData.clientAddress,
        status,
        partnerId: currentUser?.id,
        date: hasDate ? jobData.date : undefined,
        time: hasDate && jobData.time ? jobData.time.slice(0, 5) : undefined,
        stockRegionId: jobData.stockRegionId ? String(jobData.stockRegionId) : undefined,
        dossierType,
        surface: jobData.surface,
        lightingType: jobData.lightingType,
        ceilingHeight: jobData.ceilingHeight,
        estimatedLedCount: jobData.estimatedLedCount,
        dehumidifierCount: jobData.dehumidifierCount,
        timerCount: jobData.timerCount,
        needsCounter: jobData.needsCounter,
        needsGenerator: jobData.needsGenerator,
        notes: jobData.notes,
      };
      const { error } = await create(payload);
      if (error) {
        addLogEntry("Erreur", `Création dossier: ${error}`);
        alert(`Impossible de créer le dossier.\n\n${error}`);
        return { error };
      }
      addLogEntry("Nouveau dossier", hasDate ? `Créé par ${currentUser?.name} (date programmée)` : `Créé par ${currentUser?.name}`);
      return { error: null };
    },
    [create, currentUser, addLogEntry, refetchJobs, partnerInstallerJobs.refetch]
  );

  const handleAdminAddJob = useCallback(
    async (jobData: Record<string, unknown>) => {
      const payload: Omit<Job, "id" | "createdAt"> = {
        clientName: String(jobData.clientName ?? ""),
        clientPhone: String(jobData.clientPhone ?? ""),
        clientAddress: String(jobData.clientAddress ?? ""),
        status: (jobData.status as Job["status"]) ?? "pending",
        surface: jobData.surface ? String(jobData.surface) : undefined,
        lightingType: jobData.lightingType ? String(jobData.lightingType) : undefined,
        ceilingHeight: jobData.ceilingHeight ? String(jobData.ceilingHeight) : undefined,
        estimatedLedCount: jobData.estimatedLedCount ? String(jobData.estimatedLedCount) : undefined,
        notes: jobData.notes ? String(jobData.notes) : undefined,
        partnerId: jobData.partnerId ? String(jobData.partnerId) : undefined,
        installerId: jobData.installerId ? String(jobData.installerId) : undefined,
        date: jobData.date ? String(jobData.date) : undefined,
        time: jobData.time ? String(jobData.time) : undefined,
        stockToPrepare:
          jobData.stockToPrepare && typeof jobData.stockToPrepare === "object"
            ? (jobData.stockToPrepare as Record<string, number>)
            : undefined,
        stockRegionId: jobData.stockRegionId ? String(jobData.stockRegionId) : undefined,
        dossierType:
          jobData.dossierType === "led_exterieur" ||
          jobData.dossierType === "led_interieur" ||
          jobData.dossierType === "deshumidificateur" ||
          jobData.dossierType === "vmc_agricoles" ||
          jobData.dossierType === "th_122" ||
          jobData.dossierType === "renovation" ||
          jobData.dossierType === "hp_bp_flottante" ||
          jobData.dossierType === "pac"
            ? jobData.dossierType
            : "led",
        dehumidifierCount:
          typeof jobData.dehumidifierCount === "number" ? jobData.dehumidifierCount : undefined,
        timerCount: typeof jobData.timerCount === "number" ? jobData.timerCount : undefined,
        needsCounter: typeof jobData.needsCounter === "boolean" ? jobData.needsCounter : undefined,
        needsGenerator:
          typeof jobData.needsGenerator === "boolean" ? jobData.needsGenerator : undefined,
        createdBy: currentUser?.id,
      };

      const { data: newJob, error } = await create(payload);

      if (error) {
        addLogEntry("Erreur", `Création dossier: ${error}`);
        alert(`Erreur lors de la création du dossier: ${error}`);
        return;
      }
      addLogEntry("Nouveau dossier", `Créé par ${currentUser?.name ?? "Admin"}`);
      if (newJob) {
        notifyWebhooks("job.created", newJob.id, {
          id: newJob.id,
          clientName: newJob.clientName,
          status: newJob.status,
          clientAddress: newJob.clientAddress,
          date: newJob.date,
        });
      }
    },
    [create, addLogEntry, currentUser]
  );

  const handleAdminImportJobs = useCallback(
    async (jobsData: Array<Record<string, unknown>>) => {
      let created = 0;
      let failed = 0;
      for (const jobData of jobsData) {
        const payload: Omit<Job, "id" | "createdAt"> = {
          clientName: String(jobData.clientName ?? "").trim(),
          clientPhone: String(jobData.clientPhone ?? "").trim(),
          clientAddress: String(jobData.clientAddress ?? "").trim(),
          status: (jobData.status as Job["status"]) ?? "pending",
          surface: jobData.surface ? String(jobData.surface).trim() : undefined,
          lightingType: jobData.lightingType ? String(jobData.lightingType).trim() : undefined,
          ceilingHeight: jobData.ceilingHeight ? String(jobData.ceilingHeight).trim() : undefined,
          estimatedLedCount: jobData.estimatedLedCount ? String(jobData.estimatedLedCount).trim() : undefined,
          notes: jobData.notes ? String(jobData.notes).trim() : undefined,
          partnerId: jobData.partnerId ? String(jobData.partnerId) : undefined,
          installerId: jobData.installerId ? String(jobData.installerId) : undefined,
          date: jobData.date ? String(jobData.date).trim() : undefined,
          time: jobData.time ? String(jobData.time).trim() : undefined,
          stockToPrepare:
            jobData.stockToPrepare && typeof jobData.stockToPrepare === "object"
              ? (jobData.stockToPrepare as Record<string, number>)
              : undefined,
          stockRegionId: jobData.stockRegionId ? String(jobData.stockRegionId) : undefined,
          dossierType:
            jobData.dossierType === "led_exterieur" ||
            jobData.dossierType === "led_interieur" ||
            jobData.dossierType === "deshumidificateur" ||
            jobData.dossierType === "vmc_agricoles" ||
            jobData.dossierType === "th_122" ||
            jobData.dossierType === "renovation" ||
            jobData.dossierType === "hp_bp_flottante" ||
            jobData.dossierType === "pac"
              ? jobData.dossierType
              : "led",
          dehumidifierCount:
            typeof jobData.dehumidifierCount === "number" ? jobData.dehumidifierCount : undefined,
          timerCount: typeof jobData.timerCount === "number" ? jobData.timerCount : undefined,
          needsCounter: typeof jobData.needsCounter === "boolean" ? jobData.needsCounter : undefined,
          needsGenerator:
            typeof jobData.needsGenerator === "boolean" ? jobData.needsGenerator : undefined,
          createdBy: currentUser?.id,
        };
        if (!payload.clientName && !payload.clientAddress) continue;
        const { error } = await create(payload);
        if (error) failed++;
        else created++;
      }
      addLogEntry("Import", `${created} dossiers importés${failed ? `, ${failed} erreur(s)` : ""} par ${currentUser?.name ?? "Admin"}`);
    },
    [create, addLogEntry, currentUser]
  );

  const handleUpdateJob = useCallback(
    async (updatedJob: Job): Promise<{ error: string | null }> => {
      if (currentUser?.role === "commercial" && currentUser.code) {
        const { data, error: fnErr } = await supabase.functions.invoke("update-job-commercial-by-code", {
          body: {
            code: currentUser.code,
            jobId: updatedJob.id,
            ...(updatedJob.signedQuoteUrls?.length
              ? { signedQuoteUrls: updatedJob.signedQuoteUrls }
              : { clearSignedQuote: true }),
          },
        });
        const raw = (data ?? {}) as { data?: JobRow; error?: string };
        const fnError = raw.error;
        if (fnErr || fnError) {
          const msg = fnError ?? fnErr?.message ?? "Erreur inconnue";
          addLogEntry("Erreur", `Mise à jour dossier: ${msg}`);
          alert(`Impossible d'enregistrer le dossier.\n\n${msg}`);
          return { error: msg };
        }
        addLogEntry("Mise à jour", `Dossier ${updatedJob.id} modifié par ${currentUser.name}`);
        await partnerInstallerJobs.refetch();
        const row = raw.data;
        if (row) {
          notifyWebhooks("job.updated", updatedJob.id, {
            id: updatedJob.id,
            clientName: updatedJob.clientName,
            status: updatedJob.status,
            clientAddress: updatedJob.clientAddress,
            date: updatedJob.date,
          });
        }
        return { error: null };
      }

      const { data: updated, error } = await update(updatedJob);
      if (error) {
        addLogEntry("Erreur", `Mise à jour dossier: ${error}`);
        alert(`Impossible d'enregistrer le dossier.\n\n${error}`);
        return { error };
      }
      addLogEntry("Mise à jour", `Dossier ${updatedJob.id} modifié par ${currentUser?.name ?? "Utilisateur"}`);
      if (updated) {
        notifyWebhooks("job.updated", updated.id, {
          id: updated.id,
          clientName: updated.clientName,
          status: updated.status,
          clientAddress: updated.clientAddress,
          date: updated.date,
        });
      }
      return { error: null };
    },
    [update, addLogEntry, currentUser, partnerInstallerJobs]
  );

  const handlePartnerUpdateJob = useCallback(
    async (updatedJob: Job) => {
      if (currentUser?.role !== "partner" || !currentUser.code) {
        const { data: updated, error } = await update(updatedJob);
        if (error) {
          addLogEntry("Erreur", `Mise à jour dossier: ${error}`);
          return;
        }
        addLogEntry("Mise à jour", `Dossier ${updatedJob.id} modifié par ${currentUser?.name ?? "Utilisateur"}`);
        if (updated) {
          notifyWebhooks("job.updated", updated.id, {
            id: updated.id,
            clientName: updated.clientName,
            status: updated.status,
            clientAddress: updated.clientAddress,
            date: updated.date,
          });
        }
        return;
      }

      const { data, error: fnErr } = await supabase.functions.invoke("update-job-partner-by-code", {
        body: {
          code: currentUser.code,
          job: {
            id: updatedJob.id,
            clientName: updatedJob.clientName,
            clientPhone: updatedJob.clientPhone,
            clientAddress: updatedJob.clientAddress,
            surface: updatedJob.surface,
            lightingType: updatedJob.lightingType,
            ceilingHeight: updatedJob.ceilingHeight,
            estimatedLedCount: updatedJob.estimatedLedCount,
            notes: updatedJob.notes,
            stockRegionId: updatedJob.stockRegionId,
            dossierType: updatedJob.dossierType,
            // Toujours envoyer les clés pour que l’Edge sache si le RDV doit être effacé (null) ou conservé
            date: updatedJob.date ?? null,
            time: updatedJob.time ?? null,
            // Préserve les documents si présents ; clear explicite si retirés côté UI
            ...(updatedJob.signedQuoteUrls?.length
              ? { signedQuoteUrls: updatedJob.signedQuoteUrls }
              : { signedQuoteUrls: null, clearSignedQuote: true }),
          },
        },
      });

      const raw = (data ?? {}) as { data?: JobRow; error?: string };
      const fnError = raw.error;
      if (fnErr || fnError) {
        const msg = fnError ?? fnErr?.message ?? "Erreur inconnue";
        addLogEntry("Erreur", `Mise à jour dossier: ${msg}`);
        alert(`Impossible d'enregistrer les modifications.\n\n${msg}`);
        return;
      }

      await refetchJobs();
      await partnerInstallerJobs.refetch();
      addLogEntry("Mise à jour", `Dossier ${updatedJob.id} modifié par ${currentUser.name}`);
      const row = raw.data;
      if (row) {
        const j = jobRowToJob(row);
        notifyWebhooks("job.updated", j.id, {
          id: j.id,
          clientName: j.clientName,
          status: j.status,
          clientAddress: j.clientAddress,
          date: j.date,
        });
      }
    },
    [currentUser, update, addLogEntry, refetchJobs, partnerInstallerJobs.refetch]
  );

  const handlePartnerDeleteJob = useCallback(
    async (jobId: string) => {
      if (currentUser?.role !== "partner" || !currentUser.code) {
        const { error } = await remove(jobId);
        if (error) {
          addLogEntry("Erreur", `Suppression dossier: ${error}`);
          alert(`Impossible de supprimer le dossier.\n\n${error}`);
        } else {
          await refetchJobs();
          await partnerInstallerJobs.refetch();
          addLogEntry("Suppression", `Dossier ${jobId} supprimé`);
        }
        return;
      }

      const { data, error: fnErr } = await supabase.functions.invoke("delete-job-partner-by-code", {
        body: { code: currentUser.code, jobId },
      });

      const raw = (data ?? {}) as { error?: string };
      const fnError = raw.error;
      if (fnErr || fnError) {
        const msg = fnError ?? fnErr?.message ?? "Erreur inconnue";
        addLogEntry("Erreur", `Suppression dossier: ${msg}`);
        alert(`Impossible de supprimer le dossier.\n\n${msg}`);
        return;
      }

      await refetchJobs();
      await partnerInstallerJobs.refetch();
      addLogEntry("Suppression", `Dossier ${jobId} supprimé par ${currentUser.name}`);
    },
    [currentUser, remove, addLogEntry, refetchJobs, partnerInstallerJobs.refetch]
  );

  const validateJob = useCallback(
    async (
      jobId: string,
      usageNeuf: Record<string, number>,
      usageRemplacement: Record<string, number>,
      notes: string
    ) => {
      const job = jobs.find((j) => j.id === jobId);
      if (!job) throw new Error("Dossier introuvable. Veuillez actualiser la page.");
      const totalUsage: Record<string, number> = {};
      const allIds = new Set([
        ...Object.keys(usageNeuf),
        ...Object.keys(usageRemplacement),
      ]);
      for (const itemId of allIds) {
        const t = (usageNeuf[itemId] ?? 0) + (usageRemplacement[itemId] ?? 0);
        if (t > 0) totalUsage[itemId] = t;
      }
      let partnerInvoiceTotalCents = 0;
      if (job.stockRegionId) {
        for (const itemId of allIds) {
          const qtyNeuf = usageNeuf[itemId] ?? 0;
          const qtyRem = usageRemplacement[itemId] ?? 0;
          const priceNeuf = budgetPrices.getPrice(job.stockRegionId, itemId, "neuf");
          const priceRem = budgetPrices.getPrice(job.stockRegionId, itemId, "remplacement");
          partnerInvoiceTotalCents += qtyNeuf * priceNeuf + qtyRem * priceRem;
        }
      }
      try {
        const { error: upErr } = await update({
          ...job,
          status: "completed",
          stockUsed: totalUsage,
          stockUsedNeuf: usageNeuf,
          stockUsedRemplacement: usageRemplacement,
          partnerInvoiceTotalCents: partnerInvoiceTotalCents || null,
          installerNotes: notes,
        });
        if (upErr) throw new Error(upErr);
        for (const [itemId, qty] of Object.entries(totalUsage)) {
          if (qty <= 0) continue;
          const item = inventory.stock.find((s) => s.id === itemId);
          const { error: decErr } = await inventory.decrement(itemId, qty);
          if (decErr) addLogEntry("Erreur stock", `${item?.name ?? itemId}: ${decErr}`);
        }
      } catch (e) {
        addLogEntry(
          "Erreur",
          `Validation chantier: ${e instanceof Error ? e.message : "Erreur inconnue"}`
        );
        throw e;
      }
      // Rafraîchissement dans un bloc séparé : un échec de refetch ne doit pas
      // signaler un faux échec métier (le statut est déjà passé à completed en base).
      try {
        await refetchJobs();
        await partnerInstallerJobs.refetch();
      } catch (_) { /* ignore – la donnée locale se resynchronisera */ }
      addLogEntry("Installation validée", `Dossier ${jobId} terminé. Stock mis à jour.`);
      notifyWebhooks("job.completed", jobId, {
        id: job.id,
        clientName: job.clientName,
        status: "completed",
        clientAddress: job.clientAddress,
        date: job.date,
      });
    },
    [jobs, update, inventory, addLogEntry, budgetPrices.getPrice, refetchJobs, partnerInstallerJobs.refetch]
  );

  const modifyJob = useCallback(
    async (
      jobId: string,
      newUsageNeuf: Record<string, number>,
      newUsageRemplacement: Record<string, number>,
      newNotes: string
    ) => {
      const oldJob = jobs.find((j) => j.id === jobId);
      if (!oldJob) throw new Error("Dossier introuvable. Veuillez actualiser la page.");
      const oldTotal: Record<string, number> =
        oldJob.stockUsed && Object.keys(oldJob.stockUsed).length > 0
          ? oldJob.stockUsed
          : (() => {
              const o: Record<string, number> = {};
              const n = oldJob.stockUsedNeuf ?? {};
              const r = oldJob.stockUsedRemplacement ?? {};
              for (const id of new Set([...Object.keys(n), ...Object.keys(r)])) {
                const t = (n[id] ?? 0) + (r[id] ?? 0);
                if (t > 0) o[id] = t;
              }
              return o;
            })();
      const newTotal: Record<string, number> = {};
      const allIds = new Set([
        ...Object.keys(newUsageNeuf),
        ...Object.keys(newUsageRemplacement),
      ]);
      for (const itemId of allIds) {
        const t = (newUsageNeuf[itemId] ?? 0) + (newUsageRemplacement[itemId] ?? 0);
        if (t > 0) newTotal[itemId] = t;
      }
      let partnerInvoiceTotalCents = 0;
      if (oldJob?.stockRegionId) {
        for (const itemId of allIds) {
          const qtyNeuf = newUsageNeuf[itemId] ?? 0;
          const qtyRem = newUsageRemplacement[itemId] ?? 0;
          const priceNeuf = budgetPrices.getPrice(oldJob.stockRegionId, itemId, "neuf");
          const priceRem = budgetPrices.getPrice(oldJob.stockRegionId, itemId, "remplacement");
          partnerInvoiceTotalCents += qtyNeuf * priceNeuf + qtyRem * priceRem;
        }
      }
      try {
        const { error: upErr } = await update({
          ...oldJob,
          stockUsed: newTotal,
          stockUsedNeuf: newUsageNeuf,
          stockUsedRemplacement: newUsageRemplacement,
          partnerInvoiceTotalCents: partnerInvoiceTotalCents || null,
          installerNotes: newNotes,
        });
        if (upErr) throw new Error(upErr);
        const itemsById = new Map(inventory.stock.map((item) => [item.id, item]));
        const idsToAdjust = new Set([...Object.keys(oldTotal), ...Object.keys(newTotal)]);
        for (const itemId of idsToAdjust) {
          const oldQ = oldTotal[itemId] ?? 0;
          const newQ = newTotal[itemId] ?? 0;
          const delta = newQ - oldQ;
          if (delta > 0) {
            const { error } = await inventory.decrement(itemId, delta);
            if (error) addLogEntry("Erreur stock", `${itemsById.get(itemId)?.name ?? itemId}: ${error}`);
          } else if (delta < 0) {
            const { error } = await inventory.increment(itemId, -delta);
            if (error) addLogEntry("Erreur stock", `${itemsById.get(itemId)?.name ?? itemId}: ${error}`);
          }
        }
        addLogEntry(
          "Modification",
          `Rapport chantier ${jobId} modifié par ${currentUser?.name}. Stock ajusté.`
        );
        notifyWebhooks("job.updated", jobId, {
          id: oldJob.id,
          clientName: oldJob.clientName,
          status: oldJob.status,
          clientAddress: oldJob.clientAddress,
          date: oldJob.date,
        });
      } catch (e) {
        addLogEntry(
          "Erreur",
          `Modification rapport: ${e instanceof Error ? e.message : "Erreur inconnue"}`
        );
        throw e;
      }
    },
    [jobs, update, inventory, currentUser, addLogEntry, budgetPrices.getPrice]
  );

  const handleDeleteJob = useCallback(
    (jobId: string) => {
      setConfirmConfig({
        isOpen: true,
        title: "Supprimer ?",
        message: "Cette action est irréversible.",
        action: async () => {
          const { error } = await remove(jobId);
          setConfirmConfig((c) => ({ ...c, isOpen: false }));
          if (error) {
            addLogEntry("Erreur", `Suppression dossier: ${error}`);
            return;
          }
          addLogEntry("Suppression", `Dossier ${jobId} supprimé`);
        },
      });
    },
    [remove, addLogEntry]
  );

  const recalculateAllPartnerInvoices = useCallback(async (): Promise<{ updated: number; error: string | null }> => {
    const completed = jobs.filter((j) => j.status === "completed" && j.stockRegionId);
    let updated = 0;
    for (const job of completed) {
      const hasNeufRem = job.stockUsedNeuf != null || job.stockUsedRemplacement != null;
      const usageNeuf = job.stockUsedNeuf ?? {};
      const usageRemplacement = job.stockUsedRemplacement ?? {};
      const allIds = new Set([
        ...Object.keys(usageNeuf),
        ...Object.keys(usageRemplacement),
        ...(job.stockUsed ? Object.keys(job.stockUsed) : []),
      ]);
      let totalCents = 0;
      for (const itemId of allIds) {
        let qtyNeuf: number;
        let qtyRem: number;
        if (hasNeufRem) {
          qtyNeuf = usageNeuf[itemId] ?? 0;
          qtyRem = usageRemplacement[itemId] ?? 0;
        } else {
          qtyNeuf = job.stockUsed?.[itemId] ?? 0;
          qtyRem = 0;
        }
        const priceNeuf = budgetPrices.getPrice(job.stockRegionId!, itemId, "neuf");
        const priceRem = budgetPrices.getPrice(job.stockRegionId!, itemId, "remplacement");
        totalCents += qtyNeuf * priceNeuf + qtyRem * priceRem;
      }
      const { error: upErr } = await update({
        ...job,
        partnerInvoiceTotalCents: totalCents || null,
      });
      if (!upErr) updated++;
    }
    if (updated > 0) addLogEntry("Budget", `${updated} facture(s) partenaire recalculée(s).`);
    return { updated, error: null };
  }, [jobs, update, budgetPrices.getPrice, addLogEntry]);

  const onAddNewStockItem = useCallback(
    async (
      name: string,
      quantity: number,
      lowStockThreshold?: number,
      regionId?: string,
      partnerId?: string | null
    ) => {
      const { error } = await inventory.add(name, quantity, lowStockThreshold, regionId, partnerId);
      if (error) addLogEntry("Erreur stock", error);
      else addLogEntry("Stock", `Nouvelle référence ajoutée: ${name}`);
      return { error };
    },
    [inventory, addLogEntry]
  );

  const handleDeleteStockItem = useCallback(
    (id: string) => {
      const item = inventory.stock.find((s) => s.id === id);
      setConfirmConfig({
        isOpen: true,
        title: "Supprimer la référence ?",
        message: "Attention, cela retirera cet article de votre inventaire.",
        action: async () => {
          const { error } = await inventory.remove(id);
          setConfirmConfig((c) => ({ ...c, isOpen: false }));
          if (error) addLogEntry("Erreur stock", error);
          else addLogEntry("Stock", `Référence supprimée: ${item?.name ?? id}`);
        },
      });
    },
    [inventory, addLogEntry]
  );

  const onUpdateStockItem = useCallback(
    async (id: string, name: string, quantity: number, lowStockThreshold?: number) => {
      const { error } = await inventory.updateItem(id, name, quantity, lowStockThreshold);
      if (error) addLogEntry("Erreur stock", error);
      else addLogEntry("Stock", `Mise à jour référence: ${name}`);
    },
    [inventory, addLogEntry]
  );

  const handleManageAccess = useCallback((type: "partner" | "installer" | "commercial") => {
    setAccessManagerConfig({ isOpen: true, type });
  }, []);

  const handleAddAccess = useCallback(
    async (data: {
      name: string;
      code: string;
      phone?: string;
      email?: string;
      role: "partner" | "installer" | "commercial";
      active: boolean;
      defaultStockRegionId?: string;
    }) => {
      if (!ownerId) {
        addLogEntry("Erreur", "Espace non identifié.");
        return;
      }
      try {
        const { data: { session }, error: sessionErr } = await supabase.auth.refreshSession();
        if (sessionErr || !session?.access_token) {
          addLogEntry("Erreur", "Session expirée. Reconnectez-vous.");
          alert("Session expirée. Déconnectez-vous et reconnectez-vous, puis réessayez.");
          return;
        }
        const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
        const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
        if (!supabaseUrl || !anonKey) {
          addLogEntry("Erreur", "Configuration Supabase manquante.");
          alert("Configuration Supabase manquante (URL ou clé anon).");
          return;
        }
        const res = await fetch(`${supabaseUrl}/functions/v1/create-partner-installer`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
            apikey: anonKey,
          },
          body: JSON.stringify({
            name: data.name,
            code: data.code,
            phone: data.phone,
            email: data.email,
            role: data.role,
            defaultStockRegionId: data.defaultStockRegionId,
            owner_id: ownerId,
          }),
        });
        const fnData = await res.json().catch(() => ({})) as { error?: string; data?: unknown };
        if (!res.ok) {
          const errMsg =
            fnData?.error ??
            (res.status === 401 ? "Session invalide. Déconnectez-vous et reconnectez-vous, puis réessayez." : null) ??
            `Erreur ${res.status}`;
          addLogEntry("Erreur", `Création compte: ${errMsg}`);
          alert(`Impossible de créer le compte.\n\n${errMsg}`);
          return;
        }
        if (fnData?.error) {
          addLogEntry("Erreur", `Création compte: ${fnData.error}`);
          alert(`Impossible de créer le compte.\n\n${fnData.error}`);
          return;
        }
        const createdRow = fnData?.data as import("./types").ProfileRow | undefined;
        if (createdRow?.id) {
          upsertProfile(createdRow);
        }
        await refetchProfiles();
        addLogEntry("Création compte", `Nouveau ${data.role}: ${data.name}`);
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        addLogEntry("Erreur", `Création compte: ${msg}`);
        alert(`Impossible de créer le compte.\n\n${msg}`);
      }
    },
    [ownerId, addLogEntry, refetchProfiles, upsertProfile]
  );

  const handleUpdateAccess = useCallback(
    async (userId: string, data: { name?: string; email?: string; preferences?: import("./types").UserPreferences }) => {
      const { error } = await updateProfile(userId, data);
      if (error) addLogEntry("Erreur", `Modification compte: ${error}`);
      else if (data.preferences && data.name === undefined) addLogEntry("Modification compte", "Préférence poseur mise à jour");
      else addLogEntry("Modification compte", `Compte modifié: ${data.name ?? userId}`);
    },
    [updateProfile, addLogEntry]
  );

  const handleRemoveAccess = useCallback(
    async (userId: string) => {
      const { error } = await removeProfile(userId, accessManagerConfig.type);
      if (error) {
        addLogEntry("Erreur", `Suppression compte: ${error}`);
        alert(`Impossible de supprimer.\n\n${error}`);
      } else addLogEntry("Suppression compte", `Compte supprimé: ${userId}`);
    },
    [removeProfile, accessManagerConfig.type, addLogEntry]
  );

  const handleClearLogs = useCallback(async () => {
    const { error } = await clearLogs();
    if (error) addLogEntry("Erreur", `Effacement logs: ${error}`);
  }, [clearLogs, addLogEntry]);

  const handleDeleteLog = useCallback(
    async (logId: string) => {
      const { error } = await removeLog(logId);
      if (error) addLogEntry("Erreur", `Suppression log: ${error}`);
    },
    [removeLog, addLogEntry]
  );

  const runConfirm = useCallback(() => {
    const result = confirmConfig.action();
    if (result instanceof Promise) return result.catch((err) => console.error("Erreur action confirmation:", err));
  }, [confirmConfig.action]);

  const handleSetPassword = useCallback(
    async (password: string, confirmPassword: string) => {
      setSetPasswordError(null);
      if (!password || password.length < 6) {
        setSetPasswordError("Le mot de passe doit faire au moins 6 caractères.");
        return;
      }
      if (password !== confirmPassword) {
        setSetPasswordError("Les deux mots de passe ne correspondent pas.");
        return;
      }
      const { data: { user }, error } = await supabase.auth.updateUser({ password });
      if (error) {
        setSetPasswordError(error.message);
        return;
      }
      setNeedsToSetPassword(false);
      setIsPasswordRecovery(false);
      setSetPasswordError(null);
      // Après définition du mot de passe (invitation ou recovery), connecter l'utilisateur
      if (user?.id) {
        const { data: prof, error: pe } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", user.id)
          .maybeSingle();
        if (!pe && prof) {
          setCurrentUser(profileToUser(prof as ProfileRow));
          setCurrentView((prof as ProfileRow).role);
          if (
            (prof as ProfileRow).role === "partner" ||
            (prof as ProfileRow).role === "installer" ||
            (prof as ProfileRow).role === "commercial"
          ) {
            sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({
              role: (prof as ProfileRow).role,
              code: (prof as ProfileRow).code,
            }));
          }
        }
      }
    },
    []
  );

  if (!authReady) {
    return (
      <div className="font-sans min-h-screen bg-slate-100 dark:bg-slate-900 flex items-center justify-center relative">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-400/20 dark:bg-blue-600/20 blur-[120px] rounded-full" />
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-400/20 dark:bg-purple-600/20 blur-[120px] rounded-full" />
        </div>
        <p className="text-gray-600 dark:text-slate-300 relative z-10">Chargement…</p>
      </div>
    );
  }

  if (needsToSetPassword) {
    return (
      <SetPasswordScreen
        onSetPassword={handleSetPassword}
        error={setPasswordError}
        isRecovery={isPasswordRecovery}
      />
    );
  }

  return (
    <Routes>
      <Route path="/mentions-legales" element={<MentionsLegales />} />
      <Route path="/cgu" element={<CGU />} />
      <Route path="/confidentialite" element={<Confidentialite />} />
      <Route
        path="*"
        element={
    <div className="font-sans min-h-screen bg-slate-100 dark:bg-slate-900 text-gray-900 dark:text-white relative">
      {/* Ambient Blobs - fond d'ambiance */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-400/20 dark:bg-blue-600/20 blur-[120px] rounded-full" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-400/20 dark:bg-purple-600/20 blur-[120px] rounded-full" />
      </div>
      <div className="relative z-10">
      {currentView === "landing" && <LandingView onSelectRole={onSelectRole} />}
      {isConnectedView && currentUser && !subscription.loading && mustShowPaywall && ownerId && (
        <PayWall
          ownerId={ownerId}
          onLogout={handleLogout}
          onCreateCheckout={handleCreateCheckout}
        />
      )}
      {currentView === "admin" && currentUser && (subscription.loading || isAllowedAccess) && (
        <>
          {subscription.loading && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-100/90 dark:bg-slate-900/90 backdrop-blur-sm">
              <p className="text-gray-600 dark:text-slate-300 font-medium">Vérification de l&apos;abonnement…</p>
            </div>
          )}
        <AdminDashboard
          currentUser={currentUser}
          stock={inventory.stock}
          setStock={inventory.setStock}
          stockRegions={stockRegions.regions}
          stockRegionsLoading={stockRegions.loading}
          onAddRegion={async (name) => {
            const result = await stockRegions.add(name);
            if (result.data) {
              // Seed catalogue types en stock interne uniquement ; les partenaires à la demande dans Budget
              await inventory.ensureTypeCatalog(result.data.id, null);
            }
            return result;
          }}
          onUpdateRegion={stockRegions.update}
          onRemoveRegion={stockRegions.remove}
          onEnsureBudgetTypeCatalog={inventory.ensureTypeCatalog}
          refetchStockRegions={stockRegions.refetch}
          logs={logs}
          jobs={jobs}
          installers={installers}
          partners={partners}
          commercials={commercials}
          onUpdateJob={handleUpdateJob}
          onDeleteJob={handleDeleteJob}
          onLogout={handleLogout}
          onManageAccess={handleManageAccess}
          onAdminAddJob={handleAdminAddJob}
          onAdminImportJobs={handleAdminImportJobs}
          onClearLogs={handleClearLogs}
          onDeleteLog={handleDeleteLog}
          onAddNewStockItem={onAddNewStockItem}
          onDeleteStockItem={handleDeleteStockItem}
          onUpdateStockItem={onUpdateStockItem}
          isOwner={isOwner}
          teamMembers={team.members}
          teamLoading={team.loading}
          teamError={team.error}
          teamCanAddMore={team.canAddMore}
          onTeamInvite={team.addMember}
          onTeamUpdatePermissions={team.updatePermissions}
          onTeamRemoveMember={team.removeMember}
          ownerId={ownerId ?? undefined}
          onManageSubscription={ownerId ? async () => { const url = await handleCreateCheckout(ownerId); if (url) window.location.href = url; } : undefined}
          subscriptionError={subscriptionError}
          onRecalculatePartnerInvoices={recalculateAllPartnerInvoices}
          pendingNavigation={adminPendingNavigation}
          onConsumePendingNavigation={() => setAdminPendingNavigation(null)}
          onDeleteAccount={handleDeleteAccount}
          onValidateJob={validateJob}
          onModifyJob={modifyJob}
          onExportAllData={handleExportAllData}
        />
        </>
      )}
      {currentView === "partner" && currentUser && !subscription.loading && isAllowedAccess && (
        <PartnerApp
          jobs={partnerInstallerJobs.jobs}
          currentUser={currentUser}
          onAddJob={handlePartnerAddJob}
          onUpdateJob={handlePartnerUpdateJob}
          onDeleteJob={handlePartnerDeleteJob}
          onLogout={handleLogout}
          partnerName={currentUser?.company ?? currentUser?.name ?? ""}
          stockRegions={partnerStockRegions.regions}
          ownerCompanyName={ownerCompanyName}
          ownerLogoUrl={ownerLogoUrl}
          canPartnerManageOwnSchedule={currentUser.preferences?.canPartnerManageOwnSchedule === true}
        />
      )}
      {currentView === "installer" && currentUser && !subscription.loading && isAllowedAccess && (
        <InstallerApp
          jobs={partnerInstallerJobs.jobs}
          currentUser={currentUser}
          onValidateJob={validateJob}
          onModifyJob={modifyJob}
          onUpdateJob={handleUpdateJob}
          onLogout={handleLogout}
          installerName={currentUser?.name ?? ""}
          stock={inventory.stock}
          installerCanSeePartner={currentUser?.preferences?.canSeePartner !== false}
          partners={partners}
          ownerCompanyName={ownerCompanyName}
          ownerLogoUrl={ownerLogoUrl}
        />
      )}
      {currentView === "commercial" && currentUser && !subscription.loading && isAllowedAccess && (
        <CommercialApp
          jobs={partnerInstallerJobs.jobs}
          currentUser={currentUser}
          onUpdateJob={handleUpdateJob}
          onLogout={handleLogout}
          ownerCompanyName={ownerCompanyName}
          ownerLogoUrl={ownerLogoUrl}
        />
      )}

      <LoginModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        role={selectedRole}
        onSuccess={handleLoginSuccess}
      />
      <AccessManagerModal
        isOpen={accessManagerConfig.isOpen}
        type={accessManagerConfig.type}
        loadError={profilesLoadError}
        onClose={() =>
          setAccessManagerConfig((c) => ({ ...c, isOpen: false }))
        }
        items={
          accessManagerConfig.type === "partner"
            ? partners
            : accessManagerConfig.type === "commercial"
              ? commercials
              : installers
        }
        stockRegions={accessManagerConfig.type === "installer" ? stockRegions.regions : undefined}
        onAdd={handleAddAccess}
        onUpdate={handleUpdateAccess}
        onRemove={handleRemoveAccess}
      />
      <ConfirmationModal
        isOpen={confirmConfig.isOpen}
        onClose={() => setConfirmConfig((c) => ({ ...c, isOpen: false }))}
        onConfirm={runConfirm}
        title={confirmConfig.title}
        message={confirmConfig.message}
      />
      {isConnectedView && currentUser && !subscription.loading && isAllowedAccess && (
        <CRMHelpBot
          notifications={
            currentView === "admin" &&
            currentUser?.preferences?.emailNotifications !== false &&
            currentUser?.preferences?.miniSecretaryEnabled !== false
              ? computeSecretaryNotifications({
                  jobs,
                  stock: inventory.stock,
                  installers,
                  partners,
                  stockRegions: stockRegions.regions,
                })
              : undefined
          }
          onNavigateToTab={
            currentView === "admin"
              ? (tab, payload) => setAdminPendingNavigation({ tab, ...payload })
              : undefined
          }
        />
      )}
      </div>
    </div>
        }
      />
    </Routes>
  );
}
 